import { defineStore } from 'pinia';
import {
    ref, computed,
    watch,
    type Ref,
} from 'vue';
import { IModalConfig } from '@/interfaces/general/IModalConfig.ts';
import { IBreadcrumbItem } from '@/interfaces/general/IBreadcrumbItem.ts';

export const useCentralModalStore = defineStore('centralModalStore', () => {
    const modalStack = ref<Array<IModalConfig>>([]);

    const modalVisible = ref<boolean>(false);
    const modalCloseGuards = ref<Array<() => boolean>>([]);
    const navigationGuards = ref<Array<() => Promise<boolean>>>([]);

    function registerNavigationGuard (guard: () => Promise<boolean>): () => void {
        navigationGuards.value.push(guard);
        return (): void => { navigationGuards.value = navigationGuards.value.filter(item => item !== guard); };
    }

    async function shouldAllowNavigation (): Promise<boolean> {
        for (let index = navigationGuards.value.length - 1; index >= 0; index--) {
            if (!(await navigationGuards.value[index]())) return false;
        }
        return true;
    }

    function registerModalCloseGuard (guard: () => boolean): () => void {
        modalCloseGuards.value = [...modalCloseGuards.value, guard];
        return (): void => {
            modalCloseGuards.value = modalCloseGuards.value.filter((g) => g !== guard);
        };
    }

    function registerNestedModalCloseGuard (): () => void {
        return registerModalCloseGuard(() => true);
    }

    function bindNestedModalCloseGuard (nestedModalVisible: Ref<boolean>): () => void {
        let disposeGuard: (() => void) | null = null;
        const stopWatch = watch(nestedModalVisible, (isVisible) => {
            disposeGuard?.();
            disposeGuard = null;
            if (!isVisible) return;
            disposeGuard = registerNestedModalCloseGuard();
        }, { immediate: true });
        return (): void => {
            stopWatch();
            disposeGuard?.();
            disposeGuard = null;
        };
    }

    function shouldAllowClosingTopModal (): boolean {
        const guards = modalCloseGuards.value;
        if (guards.length === 0) return true;
        return guards[guards.length - 1]!();
    }

    function shouldAllowClosingModalStack (): boolean {
        const guards = modalCloseGuards.value;
        if (guards.length === 0) return true;
        return guards.every((guard) => guard());
    }

    const currentVisibleModal = computed<IModalConfig | null>(() => {
        if (modalStack.value.length === 0) return null;
        return modalStack.value[modalStack.value.length - 1];
    });

    const breadcrumbItems = computed<Array<IBreadcrumbItem>>(() => {
        const items: Array<IBreadcrumbItem> = [];
        modalStack.value.forEach((entry, index) => {
            items.push({
                label: entry.label,
                onClick: index === modalStack.value.length - 1 ? undefined : () :void => goToBreadCrumbLink(index),
                id: entry.guid,
            });
        });
        return items;
    });

    function goToBreadCrumbLink (index: number): void {
        modalStack.value.length = index + 1;
    }

    function closeCurrentModal (): void {
        if (!shouldAllowClosingTopModal()) return;
        if (modalStack.value.length > 1) {
            modalStack.value.pop();
            return;
        }
        modalStack.value = [];
    }

    function closeModalByGuid (guid: string): void {
        modalStack.value = modalStack.value.filter(x => x.guid !== guid);
    }

    function addModalToStack (config: IModalConfig): void {
        modalStack.value.push(config);
    }

    function closeAllModals (): void {
        modalStack.value = [];
    }

    watch(modalStack, () => {
        modalVisible.value = modalStack.value.length > 0;
    }, { deep: true });

    return {
        breadcrumbItems,
        modalStack,
        modalVisible,
        currentVisibleModal,
        addModalToStack,
        closeCurrentModal,
        closeAllModals,
        closeModalByGuid,
        registerModalCloseGuard,
        registerNavigationGuard,
        shouldAllowNavigation,
        bindNestedModalCloseGuard,
        shouldAllowClosingTopModal,
        shouldAllowClosingModalStack,
    };
});
